'use client';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '@/styles/slider.css';

import { usePriceFormat } from '@/hooks/numberFormats';
import useDynamicTranslation from '@/hooks/useDynamicTranslation';
import { RootState } from '@/store';
import { Product } from '@/types';
import Image from 'next/image';
import { useSelector } from 'react-redux';
import Slider from 'react-slick';
import { useState } from 'react';
import { useLocale } from 'next-intl';
import { Link } from '@/i18n/routing';
import { displayUserImg } from '@/lib/image-helper';

interface HeroSectionProps {
  productSliders: Product[] | undefined;
}

export default function HeroSection({ productSliders }: HeroSectionProps) {
  if (!productSliders) return null;

  const { dt } = useDynamicTranslation();
  const locale = useLocale();
  const general = useSelector((state: RootState) => state.settings.general);
  const [activeSlide, setActiveSlide] = useState(0);

  const settings = {
    accessibility: true,
    speed: 1000,
    autoplaySpeed: 4000,
    autoplay: true,
    dots: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    cssEase: 'ease-in-out',
    beforeChange: (_: number, next: number) => setActiveSlide(next),
  };

  return (
    <div className="slider-container">
      <Slider {...settings}>
        {productSliders.map((product: Product, index: number) => {
          const authors = product.reviews?.map((review) => review.user) || [];

          return (
            <div
              key={`${product.id}-${activeSlide}`}
              className="slider-item-container"
            >
              <div
                className={`slider-item-thumbnail ${
                  index === activeSlide ? 'animationTwo' : ''
                }`}
              >
                <Image
                  fill
                  src={product.preview_img}
                  alt={product.name}
                  style={{objectFit: 'contain'}}
                />
              </div>

              <div
                className={`slider-item-info ${
                  index === activeSlide ? 'animationOne' : ''
                }`}
              >
                <span className="subtitle">
                  {product.category?.name} <i className="fa fa-fire"></i>
                </span>
                <h2 className="title">{product.name}</h2>
                <div className="d-flex align-items-center">
                  <span className="ps-product__price sale">
                    {usePriceFormat(product.discounted_price)}
                  </span>
                  {product.discount_type !== 'none' && (
                    <span className="mx-5 ps-product__del align-self-start">
                      {usePriceFormat(product.price)}
                    </span>
                  )}
                </div>

                {product.reviews_count ? (
                  <div className="rating-container">
                    <div className="authors">
                      {authors.map((author, idx) => (
                        <div className="author-img" key={idx}>
                          <Image
                            fill
                            src={displayUserImg(author?.profile_img || null)}
                            alt="author-image"
                          />
                        </div>
                      ))}
                    </div>
                    <div className="reviews">
                      <div className="average-star">
                        {Array(Math.round(product.overall_rating))
                          .fill(0)
                          .map((_, i) => (
                            <i key={i} className="fa fa-star"></i>
                          ))}
                      </div>
                      <div className="average-rate">
                        <strong>+{product.reviews_count}</strong>
                        <span>{dt('reviews')}</span>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div>
                  <Link
                    href={`/products/${product.slug}/${product.id}`}
                    className="shop-btn ps-btn ps-btn--warning"
                  >
                    {dt('shop_now')}
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </Slider>

      <style jsx>{`
        .slider-item-container {
          direction: ${locale === 'ar' ? 'ltr' : 'rtl'} !important;
          background: url(${general.hero_section_pattern}) center center
            no-repeat;
          background-size: cover;
        }
      `}</style>
    </div>
  );
}
